/**
 * Hero — DS-core hero section in the Framer visual (dark image + gradient
 * overlay, overlaid nav, content on the left, review slider on the right).
 * Content-agnostic: every text/link/image comes in via props. Named tokens
 * only. Motion via the core `m`/Reveal primitives (transform/opacity, once).
 */
import type { ReactNode } from "react";
import { MotionProvider, Reveal } from "./motion";
import { Button } from "./Button";
import { ReviewSlider, type Review } from "./ReviewSlider";

type Cta = { label: string; to: string };

export function Hero({
  nav,
  ticker,
  eyebrow,
  title,
  paragraph,
  primaryCta,
  secondaryCta,
  reviews,
  bgImage,
  fallbackNote,
}: {
  /** Overlaid navigation, absolutely positioned at the top. */
  nav?: ReactNode;
  /** Optional promo strip rendered as a band at the top of the hero card. */
  ticker?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  paragraph: ReactNode;
  primaryCta: Cta;
  secondaryCta?: Cta;
  reviews: Review[];
  bgImage: string;
  /** Optional small note under the CTAs (e.g. the Time-Zero fallback). */
  fallbackNote?: ReactNode;
}) {
  return (
    <MotionProvider>
      <section
        aria-label="Introduction"
        className="relative flex justify-center px-4 font-ds md:px-[30px] lg:min-h-[calc(100vh-52px)]"
      >
        {/* Background — dark panel always. The photo + gradients only show on
            desktop; on mobile the hero is plain dark and the image is rendered
            below the text instead. */}
        <div className="pointer-events-none absolute inset-x-4 top-0 bottom-4 overflow-hidden rounded-3xl bg-ds-hero">
          <div
            className="absolute inset-0 hidden rounded-3xl bg-cover bg-center lg:block"
            style={{ backgroundImage: `url(${bgImage})` }}
          />
          <div
            className="absolute inset-0 hidden rounded-3xl lg:block"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.5) 100%), linear-gradient(270deg, rgba(0,0,0,0) 8%, rgba(0,0,0,0.82) 100%)",
            }}
          />
        </div>

        {/* Overlaid nav */}
        {nav && (
          <div className="absolute inset-x-0 top-0 z-20 flex justify-center px-4 md:px-[30px]">
            <div className="w-full max-w-[1312px]">{nav}</div>
          </div>
        )}

        {/* Content — stacked below lg, side-by-side row at ≥lg. The columns are
            fluid (left flex-1 min-w-0, review 42% capped at 501) so the review
            sits to the right, bottom-aligned, without overlapping at any width. */}
        {/* Content distributed top↔bottom (space-between): the main info group is
            pushed to the top, the 2027 paragraph + reviews to the bottom, inside a
            120px top/bottom padded block. */}
        <div className={`relative z-[2] flex w-full max-w-[1312px] flex-col gap-10 px-4 pb-20 ${nav ? "pt-28" : "pt-20"} md:px-10 lg:py-[120px]`}>
          {ticker && <Reveal eager>{ticker}</Reveal>}

          {/* Main info — grows to fill the hero height, content centred vertically.
              These sit above the fold and include the LCP <h1>, so they render
              eager (visible on first paint, not gated behind the JS reveal). */}
          <div className="flex max-w-[700px] flex-1 flex-col items-start justify-center gap-8 lg:gap-10">
            {eyebrow && (
              <Reveal
                eager
                as="span"
                className="inline-block rounded bg-white/10 px-3.5 py-1.5 text-ds-14 font-bold uppercase tracking-[0.04em] text-ds-on-dark backdrop-blur-[4px]"
              >
                {eyebrow}
              </Reveal>
            )}

            <Reveal
              eager
              as="h1"
              className="m-0 text-balance text-ds-38 font-semibold leading-[1.2] tracking-[-1.2px] text-ds-on-dark sm:text-ds-46 lg:text-[62px] lg:tracking-[-2px]"
            >
              {title}
            </Reveal>

            {/* CTAs + fallback note grouped so the note sits a fixed 24px under
                the buttons (rather than the section's larger gap). */}
            <div className="flex flex-col gap-6">
              <Reveal eager className="flex flex-wrap items-center gap-2.5">
                <Button variant="solid" to={primaryCta.to}>{primaryCta.label}</Button>
                {secondaryCta && <Button variant="ghost" to={secondaryCta.to}>{secondaryCta.label}</Button>}
              </Reveal>

              {fallbackNote && (
                <Reveal eager as="p" className="m-0 text-ds-14 text-white/70">
                  {fallbackNote}
                </Reveal>
              )}
            </div>
          </div>

          {/* From-2027 paragraph + reviews */}
          <div className="flex flex-col items-stretch gap-10 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
            <Reveal
              as="p"
              delay={0.18}
              className="m-0 max-w-[450px] border-l-2 border-white/20 pl-6 text-ds-14 font-semibold uppercase leading-[1.7] tracking-[0.02em] text-ds-on-dark"
            >
              {paragraph}
            </Reveal>

            {reviews.length > 0 && (
              <Reveal delay={0.22} className="hidden w-full min-w-0 flex-none lg:block lg:w-[42%] lg:max-w-[501px]">
                <ReviewSlider reviews={reviews} />
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </MotionProvider>
  );
}
